import { apiService } from '../api';
import { User } from '@/types/auth';
import { ApiResponse } from '@/types/apiResponse';
import { Gender } from '@/types/auth';
import type { ExperienceGroup } from '@/types/profile/userExperiences';
import type { SocialLink } from '@/types/socialLinks';

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  username?: string;
  is18AndAbove?: boolean;
  gender?: Gender;
  countryCode?: {
    name?: string;
    code?: string;
    abbreviation?: string;
  };
  profilePicture?: {
    name: string;
    size: number;
    type: string;
    url: string;
  };
  profilePictureUpload?: boolean;
  coverImage?: {
    name: string;
    size: number;
    type: string;
    url: string;
  };
  bannerUpload?: boolean;
  preferredLanguage?: string;
  dateOfBirth?: string;
  preferredGameIds?: string[];
  experience?: ExperienceGroup[];
  socialLinks?: SocialLink[];
  identityRoles?: string[];
}

export class ProfileService {
  private static instance: ProfileService;

  private constructor() {}

  public static getInstance(): ProfileService {
    if (!ProfileService.instance) {
      ProfileService.instance = new ProfileService();
    }
    return ProfileService.instance;
  }

  public async updateProfile(data: UpdateProfileRequest): Promise<
    ApiResponse<
      User & {
        profilePicturePresignedUrl?: string;
        coverImagePresignedUrl?: string;
        coverPresignedUrl?: string;
      }
    >
  > {
    try {
      const response = await apiService.post<
        ApiResponse<
          User & {
            profilePicturePresignedUrl?: string;
            coverImagePresignedUrl?: string;
            coverPresignedUrl?: string;
          }
        >,
        UpdateProfileRequest
      >('/private/api/v1/auth/updateProfile', data);
      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  public async resendEmailVerification(): Promise<
    ApiResponse<{ message: string }>
  > {
    try {
      const response = await apiService.get<ApiResponse<{ message: string }>>(
        '/private/api/v1/auth/resendEmailVerificationLink',
      );
      return response;
    } catch (error) {
      console.error('Resend email verification error:', error);
      throw error;
    }
  }

  public async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response = await apiService.get<ApiResponse<User>>(
        '/private/api/v1/auth/getCurrentUser',
      );
      return response;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  public async getByUsername(username: string): Promise<ApiResponse<User>> {
    try {
      const response = await apiService.get<ApiResponse<User>>(
        `/private/api/v1/auth/profile/${encodeURIComponent(username)}`,
      );
      return response;
    } catch (error) {
      console.error('Get user by username error:', error);
      throw error;
    }
  }
}

export const profileService = ProfileService.getInstance();
